import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useWavesurfer } from '@wavesurfer/react';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  SkipBack,
  SkipForward,
} from 'lucide-react';
import { cn } from '../../utils/utils';

const SPEEDS = [1, 1.25, 1.5, 2, 0.75];

const ROUND_CONTROL =
  'tap-target flex h-9 w-9 items-center justify-center rounded-full border border-foreground/10 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50';

interface AudioPlayerProps {
  url: string;
  onTimeUpdate?: (time: number) => void;
  onSeekReady?: (seekFn: (time: number) => void) => void;
  /** Notifies the parent when playback starts/stops. Used by activity
   *  tracking to gate ContentActivity rows on real playback time, not
   *  idle tab-open time. */
  onPlayingChange?: (isPlaying: boolean) => void;
  className?: string;
  waveColor?: string;
  progressColor?: string;
  height?: number;
  /**
   * Accent hex for the played part of the waveform. The transport itself is
   * always the app's black action pill, so the accent colours the content,
   * not the controls. Omit for the default indigo.
   */
  accentColor?: string;
  /**
   * Precomputed normalized (0-1) waveform peaks. When provided together with
   * `duration`, Wavesurfer renders the waveform from these instead of
   * downloading + decoding the whole file, so the waveform paints and the
   * play button lights up immediately (audio still streams via range
   * requests). Omit to fall back to client-side decoding.
   */
  peaks?: number[];
  /** Precomputed audio duration in seconds. Required alongside `peaks`. */
  duration?: number;
  /**
   * An audio element owned elsewhere (the app's one podcast player) to drive
   * instead of creating one. It keeps playing when this player unmounts, and
   * `url` should be its current source so attaching never reloads it. Needs
   * `peaks` + `duration`: without them Wavesurfer swaps the source for a
   * decoded blob, restarting playback.
   */
  media?: HTMLMediaElement;
}

export function AudioPlayer({
  url,
  onTimeUpdate,
  onSeekReady,
  onPlayingChange,
  className,
  waveColor,
  progressColor,
  height = 96,
  accentColor,
  peaks,
  duration: precomputedDuration,
  media,
}: AudioPlayerProps) {
  const isDark = useIsDark();
  // Canvas colours can't use `dark:` classes, so the defaults are
  // THEME-AWARE literals: indigo in light, violet (dark --primary) in dark for
  // the played bars. The podcast uses these rather than a per-format accent.
  // The unplayed light neutral is indigo-tinted (#CDD4E8) to sit on the
  // indigo ground the way the marketing site's waveform does, not gray-300.
  const progressAccent =
    progressColor ?? accentColor ?? (isDark ? '#ac8bf9' : '#6366f1');
  const resolvedWave = waveColor ?? (isDark ? '#3f3f46' : '#cdd4e8');
  const containerRef = useRef<HTMLDivElement>(null);
  const pixelRatio =
    typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
  // An attached element keeps its own volume and speed.
  const [volume, setVolume] = useState(() => media?.volume ?? 0.8);
  const [playbackRate, setPlaybackRate] = useState(
    () => media?.playbackRate ?? 1
  );
  const mediaPlaying = useMediaPlaying(media);
  const [error, setError] = useState<string | null>(null);

  // Only feed peaks when we have both peaks and a duration - Wavesurfer needs
  // the duration to map the precomputed envelope onto the time axis. The
  // `[peaks]` wrapper (Wavesurfer takes one array per channel) is memoized so
  // its identity is stable; @wavesurfer/react re-creates the instance whenever
  // any option value changes by identity, so an inline array would thrash it.
  const hasPeaks = !!peaks?.length && !!precomputedDuration;
  const channelPeaks = useMemo(
    () => (hasPeaks ? [peaks as number[]] : undefined),
    [hasPeaks, peaks]
  );

  const {
    wavesurfer,
    isReady: wsIsReady,
    isPlaying: wsIsPlaying,
    currentTime,
  } = useWavesurfer({
    container: containerRef,
    media,
    url,
    peaks: channelPeaks,
    duration: hasPeaks ? precomputedDuration : undefined,
    waveColor: resolvedWave,
    progressColor: progressAccent,
    height,
    // Chunky, fully rounded bars, matching the marketing waveform (~68 bars
    // across the reading column). Thin 4px bars read as a busy audio editor.
    // `normalize` scales the loudest peak to full height, so quiet recordings
    // still fill the band instead of drawing as a flat line.
    barWidth: 6,
    barGap: 2.5,
    // Wavesurfer multiplies barWidth by the device pixel ratio but draws
    // barRadius as given, so a plain `3` is only half-round on a 2x screen.
    // Scaling it the same way keeps the bar ends fully round everywhere.
    barRadius: 3 * pixelRatio,
    normalize: true,
    // No playhead line: the played/unplayed colour split already marks the
    // position, as on the marketing waveform.
    cursorWidth: 0,
    interact: true,
    dragToSeek: true,
  });

  // `@wavesurfer/react` subscribes to the 'ready' event in an effect that runs
  // on a later macrotask. When precomputed `peaks` + `duration` are supplied,
  // Wavesurfer skips the network decode and emits 'ready' on a microtask during
  // construction - BEFORE that subscription attaches - so its `isReady` can stay
  // false forever even though the waveform is decoded and the audio is playable
  // (the player looks stuck on "Loading..."). Track readiness ourselves and add
  // a catch-up check for the already-decoded state so we never miss that early
  // event. Falls back to the hook's value for the plain-URL (no-peaks) path.
  const [readyReached, setReadyReached] = useState(false);
  useEffect(() => {
    if (!wavesurfer) {
      setReadyReached(false);
      return;
    }
    const markReady = () => setReadyReached(true);
    const onLoad = () => setReadyReached(false);
    // Catch a 'ready'/'decode' that already fired before we subscribed.
    if (wavesurfer.getDecodedData()) markReady();
    wavesurfer.on('ready', markReady);
    wavesurfer.on('decode', markReady);
    wavesurfer.on('load', onLoad);
    return () => {
      wavesurfer.un('ready', markReady);
      wavesurfer.un('decode', markReady);
      wavesurfer.un('load', onLoad);
    };
  }, [wavesurfer]);

  const isReady = wsIsReady || readyReached;
  // Wavesurfer only learns the play state from later events, so an element
  // that was already playing when attached would show "Play".
  const isPlaying = media ? mediaPlaying : wsIsPlaying;

  // Error handling
  useEffect(() => {
    if (wavesurfer) {
      const onError = () => setError('Failed to load audio');
      const onReady = () => setError(null);

      wavesurfer.on('error', onError);
      wavesurfer.on('ready', onReady);

      return () => {
        wavesurfer.un('error', onError);
        wavesurfer.un('ready', onReady);
      };
    }
  }, [wavesurfer]);

  // Initialize volume when ready
  useEffect(() => {
    if (wavesurfer && isReady) {
      wavesurfer.setVolume(volume);
    }
  }, [wavesurfer, isReady, volume]);

  // Repaint the canvas waveform when the theme flips (wavesurfer holds the
  // colours internally, so a prop change alone won't recolour the bars).
  useEffect(() => {
    wavesurfer?.setOptions({
      waveColor: resolvedWave,
      progressColor: progressAccent,
    });
  }, [wavesurfer, resolvedWave, progressAccent]);

  // Update parent component with current time
  useEffect(() => {
    if (onTimeUpdate) {
      onTimeUpdate(currentTime);
    }
  }, [currentTime, onTimeUpdate]);

  // Notify parent on playback state change so it can gate activity tracking.
  useEffect(() => {
    onPlayingChange?.(isPlaying);
  }, [isPlaying, onPlayingChange]);

  const handlePlayPause = useCallback(() => {
    if (wavesurfer && isReady) {
      wavesurfer.playPause();
    }
  }, [wavesurfer, isReady]);

  const handleSeek = useCallback(
    (seconds: number) => {
      if (wavesurfer && isReady) {
        const duration = wavesurfer.getDuration();
        const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
        wavesurfer.setTime(newTime);
      }
    },
    [wavesurfer, isReady, currentTime]
  );

  const seekToTime = useCallback(
    (time: number) => {
      if (wavesurfer && isReady) {
        wavesurfer.setTime(time);
      }
    },
    [wavesurfer, isReady]
  );

  // Expose seek function to parent
  useEffect(() => {
    if (onSeekReady && isReady) {
      onSeekReady(seekToTime);
    }
  }, [onSeekReady, seekToTime, isReady]);

  const handleVolumeChange = useCallback(
    (newVolume: number) => {
      setVolume(newVolume);
      if (wavesurfer && isReady) {
        wavesurfer.setVolume(newVolume);
      }
    },
    [wavesurfer, isReady]
  );

  // One chip that steps through the rates instead of a pop-up menu: the
  // common case is nudging speed up once, and a menu hid that behind two clicks.
  const cycleSpeed = useCallback(() => {
    const next =
      SPEEDS[(SPEEDS.indexOf(playbackRate) + 1) % SPEEDS.length] ?? 1;
    setPlaybackRate(next);
    if (wavesurfer && isReady) {
      wavesurfer.setPlaybackRate(next);
    }
  }, [wavesurfer, isReady, playbackRate]);

  const toggleMute = useCallback(() => {
    handleVolumeChange(volume === 0 ? 0.8 : 0);
  }, [volume, handleVolumeChange]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const duration = wavesurfer?.getDuration() || precomputedDuration || 0;

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Waveform, straight on the panel: no inset box around it. */}
      <div className='relative'>
        {!isReady && !error && (
          <div className='absolute inset-0 flex items-center justify-center'>
            <div className='text-muted-foreground text-sm'>
              Loading audio visualizer...
            </div>
          </div>
        )}
        {error && (
          <div className='absolute inset-0 flex items-center justify-center'>
            <div className='text-destructive text-sm'>{error}</div>
          </div>
        )}
        <div
          ref={containerRef}
          className='w-full'
          style={{ opacity: isReady ? 1 : 0 }}
        />
      </div>

      {/* Elapsed and total under the two ends of the waveform. */}
      <div className='text-muted-foreground/90 mt-2 flex items-center justify-between font-mono text-xs'>
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>

      <div className='mt-4 flex items-center gap-3'>
        <button
          aria-label={isPlaying ? 'Pause' : 'Play'}
          onClick={handlePlayPause}
          disabled={!isReady}
          className='bg-primary text-primary-foreground hover:bg-primary-hover flex h-11 w-11 items-center justify-center rounded-full shadow-sm transition-colors duration-200 disabled:opacity-50'
        >
          {isPlaying ? (
            <Pause className='h-4 w-4' />
          ) : (
            <Play className='h-4 w-4' />
          )}
        </button>
        <button
          aria-label='Skip back 10 seconds'
          onClick={() => handleSeek(-10)}
          disabled={!isReady}
          className={ROUND_CONTROL}
        >
          <SkipBack className='h-3.5 w-3.5' />
        </button>
        <button
          aria-label='Skip forward 10 seconds'
          onClick={() => handleSeek(10)}
          disabled={!isReady}
          className={ROUND_CONTROL}
        >
          <SkipForward className='h-3.5 w-3.5' />
        </button>
        <button
          aria-label={`Playback speed ${playbackRate}x`}
          onClick={cycleSpeed}
          disabled={!isReady}
          className='border-foreground/10 text-muted-foreground hover:bg-muted hover:text-foreground tap-target ml-1 min-w-[3rem] rounded-md border px-2 py-1 font-mono text-xs transition-colors disabled:opacity-50'
        >
          {playbackRate}x
        </button>
        {/* Mute only. A volume slider duplicated the device's own control. */}
        <button
          aria-label={volume === 0 ? 'Unmute' : 'Mute'}
          onClick={toggleMute}
          disabled={!isReady}
          className={cn(ROUND_CONTROL, 'ml-auto border-transparent')}
        >
          {volume === 0 ? (
            <VolumeX className='h-4 w-4' />
          ) : (
            <Volume2 className='h-4 w-4' />
          )}
        </button>
      </div>
    </div>
  );
}

/** Whether an attached media element is playing (false when none is). */
function useMediaPlaying(media: HTMLMediaElement | undefined): boolean {
  const [playing, setPlaying] = useState(() => !!media && !media.paused);
  useEffect(() => {
    if (!media) return;
    const sync = () => setPlaying(!media.paused);
    sync();
    const events = ['play', 'pause', 'ended', 'emptied'] as const;
    events.forEach(e => media.addEventListener(e, sync));
    return () => events.forEach(e => media.removeEventListener(e, sync));
  }, [media]);
  return playing;
}

/**
 * Track the app's dark mode by watching the `.dark` class the ThemeProvider
 * toggles on <html>. Used to theme the canvas waveform + slider, which take
 * literal colours rather than CSS classes.
 */
function useIsDark(): boolean {
  const [isDark, setIsDark] = useState(
    () =>
      typeof document !== 'undefined' &&
      document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setIsDark(el.classList.contains('dark'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}
